/**
 * useSignInAuth hook
 * Manages authentication state and handlers for sign-in page (Supabase Auth)
 */

import { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import { Platform, Linking } from 'react-native'
import * as WebBrowser from 'expo-web-browser'
import { useRouter, useLocalSearchParams } from 'expo-router'
import { supabase } from '../../lib/supabase'
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext'
import {
  deletionJobStatusBlocksApp,
  fetchAccountDeletionJobStatusWithRetries,
  signInBannerForBlockedDeletionJobStatus,
} from '../helpers/accountDeletionStatusApi.helper'
import { setPendingSignInBannerMessage } from '../helpers/signInBannerTransfer.helper'
import { useDialog } from '../../shared/hooks/useDialog'
import { useAnalytics } from '../../analytics/useAnalytics'
import { ANALYTICS_EVENTS } from '../../analytics/events'
import { logger } from '../../helpers/logger.helper'
import { authLiveDebug } from '../helpers/authLiveDebug.helper'
import { getAuthUserObservabilityFields } from '../../shared/helpers/authObservability.helper'
import {
  GOOGLE_OAUTH_QUERY_PARAMS,
  OAUTH_FLOW_STORAGE_KEY,
  OAUTH_POST_AUTH_PATH_STORAGE_KEY,
} from '../constants/oauth.constants'
import { PASSWORD_RESET_COOLDOWN_MS, PASSWORD_RESET_COOLDOWN_MESSAGE } from '../constants/passwordReset.constants'
import {
  validateCredentials,
  validateEmailFormat,
  validatePassword,
  getOAuthRedirectUrls,
  getPasswordResetRedirectUrl,
  parseSupabaseOAuthRedirectUrl,
  getUserFriendlyErrorMessage,
  mapAuthErrorToField,
  showAuthError,
} from '../helpers/signInApi'
import { sanitizeEmailInput } from '../../helpers/sanitizeInput.helper'
import { getSafePostAuthPath } from '../helpers/postAuthRedirect.helper'
import { savePendingB2bJoinFromReturnPath } from '../../join/helpers/pendingB2bJoin.helper'
import {
  savePendingWcSquadJoinFromReturnPath,
  appendWcJoinToOAuthRedirect,
} from '../../join/helpers/pendingWcSquadJoin.helper'
import { isPublicAuthSignupQueryValue } from '../constants/publicAuthRoutes.constants'
import { OAUTH_SIGNUP_CONSENT_STORAGE_KEY } from '../constants/signupConsent.constants'
import { syncSignupPrivacyConsents } from '../helpers/syncSignupPrivacyConsents.helper'
import {
  APPLE_SIGN_IN_CANCELED,
  getAppleIdentityToken,
  isAppleNativeSignInSupported,
  isAppleSignInUiSupported,
} from '../helpers/appleAuth.helper'
import { isTransientAuthError, withTransientAuthRetry } from '../helpers/transientAuthError.helper'
import { clearStaleLocalSessionBeforeOAuth } from '../helpers/clearStaleOAuthSession.helper'
import { exchangeOAuthCodeForSessionOnce } from '../helpers/oauthPkceExchange.helper'
import {
  clearOAuthAutoRetryState,
  persistOAuthRestartConfig,
  type OAuthRestartConfig,
} from '../helpers/oauthAutoRetry.helper'
import type { UseSignInAuthReturn } from '../types/signIn.types'
import type { UseCaptchaTokenReturn } from '../../auth/turnstile/useCaptchaToken'

// #4: single source of truth for the empty errors shape
const INITIAL_ERRORS = {
  email: '',
  password: '',
  verificationCode: '',
  secondFactorCode: '',
  general: '',
  terms: '',
  passwordReset: '',
}

interface UseSignInAuthProps {
  getCaptchaToken: UseCaptchaTokenReturn['getCaptchaToken']
  resetCaptcha: UseCaptchaTokenReturn['resetCaptcha']
}

export function useSignInAuth({ getCaptchaToken, resetCaptcha }: UseSignInAuthProps): UseSignInAuthReturn {
  const { isSignedIn, isLoaded, signOut, isAnonymous, session } = useSupabaseAuth()
  const authUserDebugFields = useCallback(
    () => getAuthUserObservabilityFields(session?.user ?? null),
    [session?.user],
  )
  const { showAlert } = useDialog()
  const { track } = useAnalytics()
  const router = useRouter()
  const searchParams = useLocalSearchParams<{ returnUrl?: string | string[]; signup?: string | string[] }>()
  const postAuthPath = useMemo(() => {
    const raw = searchParams.returnUrl
    const v = typeof raw === 'string' ? raw : Array.isArray(raw) ? raw[0] : undefined
    return getSafePostAuthPath(v)
  }, [searchParams.returnUrl])

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [facebookLoading, setFacebookLoading] = useState(false)
  const [appleLoading, setAppleLoading] = useState(false)
  const [microsoftLoading, setMicrosoftLoading] = useState(false)
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false)
  const [isSignUp, setIsSignUp] = useState(() => isPublicAuthSignupQueryValue(searchParams.signup))
  const [awaitingVerification, setAwaitingVerification] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [awaitingSecondFactor, setAwaitingSecondFactor] = useState(false)
  const [secondFactorCode, setSecondFactorCode] = useState('')
  const [secondFactorStrategy, setSecondFactorStrategy] = useState<'totp' | 'phone_code' | 'email_code' | null>(null)
  const [awaitingPasswordReset, setAwaitingPasswordReset] = useState(false)
  const [switchedToSignInBanner, setSwitchedToSignInBanner] = useState<string | null>(null)
  const [errors, setErrors] = useState(INITIAL_ERRORS)
  const [marketingEmailOptIn, setMarketingEmailOptIn] = useState(false)
  const [legalTermsAccepted, setLegalTermsAccepted] = useState(false)

  // Survives resetFlowState so cooldown persists across "Back to Sign In" navigations
  const lastPasswordResetRequest = useRef<{ email: string; requestedAt: number } | null>(null)
  const oauthInFlightRef = useRef(false)
  // True once verifyOtp(type:'recovery') has consumed the single-use code and a recovery session
  // is live. Lets the user retry updateUser after a failure WITHOUT needing a fresh code, and
  // tells the back handler to sign that transient session out.
  const recoverySessionEstablishedRef = useRef(false)

  useEffect(() => {
    setIsSignUp(isPublicAuthSignupQueryValue(searchParams.signup))
  }, [searchParams.signup])

  useEffect(() => {
    if (!isSignUp) {
      setLegalTermsAccepted(false)
    }
  }, [isSignUp])

  const handleEmailChange = useCallback((newEmail: string) => {
    setEmail(newEmail)
    setErrors(prev => (prev.email ? { ...prev, email: '' } : prev))
    setSwitchedToSignInBanner(null)
  }, [])

  const handlePasswordChange = useCallback((newPassword: string) => {
    setPassword(newPassword)
    setErrors(prev => (prev.password ? { ...prev, password: '' } : prev))
  }, [])

  const handleVerificationCodeChange = useCallback((newCode: string) => {
    setVerificationCode(newCode)
    setErrors(prev => (prev.verificationCode ? { ...prev, verificationCode: '' } : prev))
  }, [])

  const handleSecondFactorCodeChange = useCallback((newCode: string) => {
    setSecondFactorCode(newCode)
    setErrors(prev => (prev.secondFactorCode ? { ...prev, secondFactorCode: '' } : prev))
  }, [])

  // #3 + #6: centralised flow reset — used by all "back" handlers and mode toggle.
  // Clears every flow flag, code, and error. Intentionally does NOT clear email
  // so users don't have to retype it when navigating back.
  const resetFlowState = useCallback(() => {
    setPassword('')
    setAwaitingVerification(false)
    setVerificationCode('')
    setAwaitingSecondFactor(false)
    setSecondFactorCode('')
    setSecondFactorStrategy(null)
    setAwaitingPasswordReset(false)
    recoverySessionEstablishedRef.current = false
    setErrors(INITIAL_ERRORS)
  }, [])

  const replaceAppIfAccountDeletionAllows = useCallback(
    async (accessToken: string | null | undefined) => {
      authLiveDebug('replaceAppIfAccountDeletionAllows start', {
        hasToken: !!accessToken,
        postAuthPath,
        ...authUserDebugFields(),
      })
      if (!accessToken) {
        authLiveDebug('replaceAppIfAccountDeletionAllows no token — replace anyway', { postAuthPath })
        router.replace(postAuthPath as never)
        return
      }
      const t0 = Date.now()
      const result = await fetchAccountDeletionJobStatusWithRetries(accessToken)
      authLiveDebug('replaceAppIfAccountDeletionAllows deletion check done', {
        ok: result.ok,
        jobStatus: result.ok ? result.jobStatus : undefined,
        ms: Date.now() - t0,
      })
      if (
        result.ok &&
        result.jobStatus !== null &&
        deletionJobStatusBlocksApp(result.jobStatus, result.lastErrorCode)
      ) {
        authLiveDebug('replaceAppIfAccountDeletionAllows BLOCKED — signOut', {
          jobStatus: result.jobStatus,
        })
        await signOut()
        setPendingSignInBannerMessage(
          signInBannerForBlockedDeletionJobStatus(result.jobStatus, result.lastErrorCode)
        )
        return
      }
      authLiveDebug('replaceAppIfAccountDeletionAllows router.replace', { postAuthPath })
      router.replace(postAuthPath as never)
    },
    [router, signOut, postAuthPath, authUserDebugFields]
  )

  const notifyConsentSyncFailed = useCallback(async () => {
    await showAlert({
      title: 'Preferences not saved',
      message:
        'Your account is ready, but we could not save your Terms acceptance or marketing choice. You can update marketing email consent in Privacy controls.',
      type: 'default',
    })
  }, [showAlert])

  const postSignupPrivacyConsentsIfNeeded = useCallback(
    async (accessToken: string | null | undefined) => {
      if (!accessToken || !legalTermsAccepted) return
      const result = await syncSignupPrivacyConsents(accessToken, {
        marketingEmailOptIn,
        legalTermsAccepted: true,
      })
      if (!result.ok) await notifyConsentSyncFailed()
    },
    [legalTermsAccepted, marketingEmailOptIn, notifyConsentSyncFailed],
  )

  const handleEmailAuth = useCallback(async () => {
    logger.debug('handleEmailAuth start', { isSignUp })
    setErrors(INITIAL_ERRORS) // #4

    if (isSignUp && !legalTermsAccepted) {
      setErrors(prev => ({
        ...prev,
        terms: 'Please accept the Terms of Service and Privacy Policy to create an account.',
      }))
      return
    }

    const validation = validateCredentials(email, password, isSignUp)
    if (!validation.isValid) {
      setErrors(prev => ({
        ...prev,
        email: validation.emailError || '',
        password: validation.passwordError || '',
      }))
      return
    }

    try {
      setLoading(true)
      const captchaToken = await getCaptchaToken()
      if (isSignUp) {
        // In production use PRODUCTION_APP_ORIGIN so confirmation links go to canonical URL, not localhost
        const emailRedirectTo = Platform.OS === 'web' ? getOAuthRedirectUrls()?.redirectUrlComplete ?? undefined : undefined

        /** Same Supabase user id — upgrade anonymous session instead of signUp (new user). Preserves DB rows keyed by user id. */
        if (isAnonymous) {
          const { error } = await supabase.auth.updateUser(
            {
              email: sanitizeEmailInput(email),
              password,
            },
            {
              ...(emailRedirectTo ? { emailRedirectTo } : {}),
            }
          )
          if (error) throw error
          const { data: sess } = await supabase.auth.getSession()
          const session = sess.session ?? null
          if (session?.access_token) {
            await postSignupPrivacyConsentsIfNeeded(session.access_token)
            track(ANALYTICS_EVENTS.GUEST_CONVERTED, { method: 'email' })
            track(ANALYTICS_EVENTS.SIGN_UP_COMPLETED)
            await replaceAppIfAccountDeletionAllows(session.access_token)
            return
          }
          setAwaitingVerification(true)
          return
        }

        const { data, error } = await supabase.auth.signUp({
          email: sanitizeEmailInput(email),
          password,
          options: {
            captchaToken,
            ...(emailRedirectTo ? { emailRedirectTo } : {}),
          },
        })
        if (error) throw error
        // Supabase silently handles duplicate sign-ups when email confirmation is enabled:
        // returns a fake user with an empty identities array instead of throwing an error.
        if (data.user?.identities !== undefined && data.user.identities.length === 0) {
          throw new Error('user already registered')
        }
        if (data.session) {
          await postSignupPrivacyConsentsIfNeeded(data.session.access_token)
          track(ANALYTICS_EVENTS.SIGN_UP_COMPLETED)
          await replaceAppIfAccountDeletionAllows(data.session.access_token)
          return
        }
        // Email confirmation required
        setAwaitingVerification(true)
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: sanitizeEmailInput(email),
          password,
          options: { captchaToken },
        })
        if (error) throw error
        if (data.session) {
          track(ANALYTICS_EVENTS.SIGN_IN_COMPLETED)
          await replaceAppIfAccountDeletionAllows(data.session.access_token)
          return
        }
        setErrors(prev => ({ ...prev, general: 'Sign-in did not return a session. Please try again.' }))
      }
    } catch (error) {
      const errorMessage = getUserFriendlyErrorMessage(error)
      // When signing up with an already-registered email (and it's not an anonymous upgrade),
      // switch to sign-in mode so the user can sign in or use "Forgot password?".
      if (isSignUp && !isAnonymous && errorMessage === 'An account with this email already exists') {
        setIsSignUp(false)
        setPassword('')
        setSwitchedToSignInBanner(
          'This email is already registered. Sign in below, or tap "Forgot password?" if you\'ve forgotten it.'
        )
        return
      }
      const field = mapAuthErrorToField(error)
      if (field === 'email') setErrors(prev => ({ ...prev, email: errorMessage }))
      else if (field === 'password') setErrors(prev => ({ ...prev, password: errorMessage }))
      else setErrors(prev => ({ ...prev, general: errorMessage }))
    } finally {
      resetCaptcha()
      setLoading(false)
    }
  }, [
    email,
    password,
    isSignUp,
    isAnonymous,
    router,
    track,
    replaceAppIfAccountDeletionAllows,
    getCaptchaToken,
    resetCaptcha,
    postSignupPrivacyConsentsIfNeeded,
    legalTermsAccepted,
  ])

  const handleVerificationCode = useCallback(async () => {
    logger.debug('handleVerificationCode start')
    setErrors(prev => ({ ...prev, verificationCode: '', general: '' }))
    if (!verificationCode?.trim()) {
      setErrors(prev => ({ ...prev, verificationCode: 'Please enter the verification code' }))
      return
    }
    if (verificationCode.length !== 6 || !/^\d+$/.test(verificationCode)) {
      setErrors(prev => ({ ...prev, verificationCode: 'Please enter a valid 6-digit code' }))
      return
    }

    try {
      setLoading(true)
      const captchaToken = await getCaptchaToken()
      const { data, error } = await supabase.auth.verifyOtp({
        email: sanitizeEmailInput(email),
        token: verificationCode,
        type: 'email',
        options: { captchaToken },
      })
      if (error) throw error
      if (data.session) {
        await postSignupPrivacyConsentsIfNeeded(data.session.access_token)
        track(ANALYTICS_EVENTS.SIGN_UP_COMPLETED)
        await replaceAppIfAccountDeletionAllows(data.session.access_token)
      } else {
        setErrors(prev => ({ ...prev, verificationCode: 'The verification code is incorrect. Please try again.' }))
        setVerificationCode('')
      }
    } catch (error) {
      const errorMessage = getUserFriendlyErrorMessage(error)
      setErrors(prev => ({ ...prev, verificationCode: errorMessage }))
      setVerificationCode('')
    } finally {
      resetCaptcha()
      setLoading(false)
    }
  }, [
    email,
    verificationCode,
    router,
    track,
    replaceAppIfAccountDeletionAllows,
    getCaptchaToken,
    resetCaptcha,
    postSignupPrivacyConsentsIfNeeded,
  ])

  const handleSecondFactor = useCallback(async () => {
    logger.debug('handleSecondFactor start', { secondFactorStrategy })
    setErrors(prev => ({ ...prev, secondFactorCode: '', general: '' }))
    if (!secondFactorCode?.trim() || !secondFactorStrategy) {
      setErrors(prev => ({ ...prev, secondFactorCode: 'Please enter the authentication code' }))
      return
    }
    try {
      setLoading(true)
      const captchaToken = await getCaptchaToken()
      const { data: otpData, error: otpErr } = await supabase.auth.verifyOtp({
        email: sanitizeEmailInput(email),
        token: secondFactorCode,
        type: 'email',
        options: { captchaToken },
      })
      if (otpErr) throw otpErr
      track(ANALYTICS_EVENTS.SIGN_IN_COMPLETED)
      const accessToken =
        otpData.session?.access_token ??
        (await supabase.auth.getSession()).data.session?.access_token
      await replaceAppIfAccountDeletionAllows(accessToken)
    } catch (error) {
      const errorMessage = getUserFriendlyErrorMessage(error)
      setErrors(prev => ({ ...prev, secondFactorCode: errorMessage }))
      setSecondFactorCode('')
    } finally {
      resetCaptcha()
      setLoading(false)
    }
  }, [email, secondFactorCode, secondFactorStrategy, router, track, replaceAppIfAccountDeletionAllows, getCaptchaToken, resetCaptcha])

  /**
   * Completes a Supabase OAuth provider sign-in once `signInWithOAuth`/`linkIdentity` returns
   * a `data.url`. Shared by Google, Facebook, and (web/Android) Apple so the redirect handling
   * lives in one place.
   * - Web: persists consent + flow state, then redirects the browser to the provider URL.
   * - Native: opens the system auth browser and completes the session (implicit or PKCE).
   * Returns `true` when the flow was started/handled, `false` when there was no URL to act on
   * (so the caller can surface a "could not start" error).
   */
  const completeOAuthBrowserOrWebRedirect = useCallback(
    async (
      data: { url?: string | null } | null | undefined,
      redirectTo: string | undefined,
      restartConfig: OAuthRestartConfig | null,
    ): Promise<boolean> => {
      authLiveDebug('completeOAuthBrowserOrWebRedirect start', {
        platform: Platform.OS,
        hasUrl: !!data?.url,
        redirectTo,
        isSignUp,
        postAuthPath,
        provider: restartConfig?.provider,
      })
      await savePendingB2bJoinFromReturnPath(postAuthPath)
      await savePendingWcSquadJoinFromReturnPath(postAuthPath)
      if (Platform.OS === 'web' && data?.url) {
        try {
          if (typeof sessionStorage !== 'undefined') {
            if (isSignUp) {
              sessionStorage.setItem(
                OAUTH_SIGNUP_CONSENT_STORAGE_KEY,
                JSON.stringify({ marketingEmailOptIn, legalTermsAccepted }),
              )
            } else {
              sessionStorage.removeItem(OAUTH_SIGNUP_CONSENT_STORAGE_KEY)
            }
            sessionStorage.setItem(OAUTH_FLOW_STORAGE_KEY, isSignUp ? 'sign_up' : 'sign_in')
            if (isAnonymous && isSignUp) {
              sessionStorage.setItem('zporti_oauth_was_guest', '1')
            }
            sessionStorage.setItem(OAUTH_POST_AUTH_PATH_STORAGE_KEY, postAuthPath)
            if (restartConfig) {
              persistOAuthRestartConfig(restartConfig)
            }
          }
        } catch {
          // ignore storage errors
        }
        authLiveDebug('completeOAuthBrowserOrWebRedirect web — redirecting to Google', {
          redirectTo,
          postAuthPath,
        })
        window.location.href = data.url
        return true
      }
      if (Platform.OS !== 'web' && data?.url && redirectTo) {
        authLiveDebug('completeOAuthBrowserOrWebRedirect native — openAuthSessionAsync')
        const result = await WebBrowser.openAuthSessionAsync(data.url, redirectTo)
        authLiveDebug('completeOAuthBrowserOrWebRedirect native browser result', {
          type: result.type,
          hasUrl: 'url' in result ? !!result.url : false,
        })
        if (result.type === 'success' && result.url) {
          // Implicit flow: tokens in hash fragment
          const tokens = parseSupabaseOAuthRedirectUrl(result.url)
          if (tokens) {
            authLiveDebug('completeOAuth native implicit — setSession')
            const { error: sessionError } = await supabase.auth.setSession(tokens)
            // Throw (don't swallow) so handleGoogleAuth's catch surfaces the failure
            // instead of leaving the user stranded on sign-in with no session and no message.
            if (sessionError) {
              authLiveDebug('completeOAuth native setSession FAILED', { message: sessionError.message })
              logger.error('OAuth setSession failed (native implicit flow)', { message: sessionError.message })
              throw sessionError
            }
            authLiveDebug('completeOAuth native implicit — setSession OK')
            await postSignupPrivacyConsentsIfNeeded(tokens.access_token)
            if (isAnonymous && isSignUp) track(ANALYTICS_EVENTS.GUEST_CONVERTED, { method: 'oauth' })
            track(isSignUp ? ANALYTICS_EVENTS.SIGN_UP_COMPLETED : ANALYTICS_EVENTS.SIGN_IN_COMPLETED)
            await replaceAppIfAccountDeletionAllows(tokens.access_token)
            return true
          }
          // PKCE: auth code in query string (when flowType is pkce)
          if (result.url.includes('code=')) {
            authLiveDebug('completeOAuth native PKCE — exchangeCodeForSessionOnce')
            const exchangeResult = await exchangeOAuthCodeForSessionOnce(result.url)
            if (exchangeResult.error) {
              authLiveDebug('completeOAuth native exchange FAILED', { message: exchangeResult.error.message })
              logger.error('OAuth exchangeCodeForSessionOnce failed (native PKCE flow)', {
                message: exchangeResult.error.message,
              })
              throw exchangeResult.error
            }
            authLiveDebug('completeOAuth native PKCE — exchange OK', { skipped: exchangeResult.skipped })
            const { data: sessionData } = await supabase.auth.getSession()
            const accessToken = sessionData?.session?.access_token ?? ''
            if (accessToken) await postSignupPrivacyConsentsIfNeeded(accessToken)
            if (isAnonymous && isSignUp) track(ANALYTICS_EVENTS.GUEST_CONVERTED, { method: 'oauth' })
            track(isSignUp ? ANALYTICS_EVENTS.SIGN_UP_COMPLETED : ANALYTICS_EVENTS.SIGN_IN_COMPLETED)
            await replaceAppIfAccountDeletionAllows(accessToken)
            return true
          }
        }
        // Browser opened but cancelled/dismissed (no success URL). This is ambiguous: a genuine
        // user cancel and a 2FA-backgrounding that tore down the auth session both report `dismiss`.
        // Before giving up, re-check for a session — Supabase's app-state/deep-link handling can
        // land the session even when WebBrowser still reports `dismiss`. If one exists, honor it
        // instead of silently bouncing the user back to sign-in.
        authLiveDebug('completeOAuth native dismiss/cancel — rechecking session')
        const { data: dismissSessionData } = await supabase.auth.getSession()
        const dismissAccessToken = dismissSessionData?.session?.access_token
        if (dismissAccessToken) {
          authLiveDebug('completeOAuth native dismiss — recovered session')
          await postSignupPrivacyConsentsIfNeeded(dismissAccessToken)
          if (isAnonymous && isSignUp) track(ANALYTICS_EVENTS.GUEST_CONVERTED, { method: 'oauth' })
          track(isSignUp ? ANALYTICS_EVENTS.SIGN_UP_COMPLETED : ANALYTICS_EVENTS.SIGN_IN_COMPLETED)
          await replaceAppIfAccountDeletionAllows(dismissAccessToken)
          return true
        }
        authLiveDebug('completeOAuth native dismiss — no session recovered')
        // No session recovered — treat as a normal cancel so no error alert fires.
        return true
      }
      if (Platform.OS !== 'web' && data?.url) {
        authLiveDebug('completeOAuth native fallback — Linking.openURL')
        await Linking.openURL(data.url)
        return true
      }
      authLiveDebug('completeOAuthBrowserOrWebRedirect — no URL, returning false')
      return false
    },
    [
      isSignUp,
      marketingEmailOptIn,
      legalTermsAccepted,
      postAuthPath,
      postSignupPrivacyConsentsIfNeeded,
      track,
      replaceAppIfAccountDeletionAllows,
    ],
  )

  const handleGoogleAuth = useCallback(async () => {
    setErrors(INITIAL_ERRORS) // #4
    authLiveDebug('handleGoogleAuth CLICK')
    if (oauthInFlightRef.current) return
    oauthInFlightRef.current = true
    try {
      setGoogleLoading(true)
      if (isSignUp && !legalTermsAccepted) {
        authLiveDebug('handleGoogleAuth blocked — terms not accepted')
        await showAlert({
          title: 'Terms required',
          message: 'Please accept the Terms of Service and Privacy Policy before continuing with Google.',
          type: 'default',
        })
        return
      }
      await clearStaleLocalSessionBeforeOAuth()
      clearOAuthAutoRetryState()
      // In production use PRODUCTION_APP_ORIGIN so Supabase redirects to canonical URL, not localhost
      // Web invite flows: carry the WC squad-join code on the redirect URL so it survives a
      // cross-app OAuth handoff (in-app browser → Safari). No-op (clean URL) for normal sign-ins.
      const redirectTo = appendWcJoinToOAuthRedirect(
        getOAuthRedirectUrls()?.redirectUrl ?? undefined,
        postAuthPath,
      )
      const googleOAuthOpts = {
        provider: 'google' as const,
        options: {
          ...(redirectTo ? { redirectTo } : {}),
          queryParams: GOOGLE_OAUTH_QUERY_PARAMS,
        },
      }
      const useLinkIdentity = isAnonymous && isSignUp
      const flow = useLinkIdentity ? 'linkIdentity' : 'signInWithOAuth'
      authLiveDebug('handleGoogleAuth initiating', {
        isSignUp,
        isAnonymous,
        flow,
        redirectTo,
        postAuthPath,
      })
      logger.info('[handleGoogleAuth] initiating OAuth', {
        isSignUp, isAnonymous, flow,
        redirectTo,
      })
      const t0 = Date.now()
      const { data, error } =
        useLinkIdentity
          ? await supabase.auth.linkIdentity(googleOAuthOpts)
          : await supabase.auth.signInWithOAuth(googleOAuthOpts)
      authLiveDebug('handleGoogleAuth supabase response', {
        ok: !error,
        hasUrl: !!data?.url,
        ms: Date.now() - t0,
        errorMessage: error?.message,
        ...authUserDebugFields(),
      })
      logger.info('[handleGoogleAuth] supabase call done', {
        ok: !error, hasUrl: !!data?.url, ms: Date.now() - t0,
      })
      if (error) throw error
      const handled = await completeOAuthBrowserOrWebRedirect(data, redirectTo, {
        provider: 'google',
        queryParams: GOOGLE_OAUTH_QUERY_PARAMS,
        useLinkIdentity,
      })
      authLiveDebug('handleGoogleAuth completeOAuth handled', { handled })
      if (!handled) {
        await showAlert({ title: 'Error', message: 'Google sign-in could not start. Please try again.' })
      }
    } catch (error) {
      authLiveDebug('handleGoogleAuth ERROR', {
        message: error instanceof Error ? error.message : String(error),
      })
      logger.error('[handleGoogleAuth] error', {
        message: error instanceof Error ? error.message : String(error),
      })
      await showAuthError(error, 'Google sign-in', showAlert)
    } finally {
      oauthInFlightRef.current = false
      setGoogleLoading(false)
    }
  }, [
    showAlert,
    isSignUp,
    isAnonymous,
    legalTermsAccepted,
    completeOAuthBrowserOrWebRedirect,
    postAuthPath,
  ])

  const handleFacebookAuth = useCallback(async () => {
    setErrors(INITIAL_ERRORS) // #4
    if (oauthInFlightRef.current) return
    oauthInFlightRef.current = true
    try {
      setFacebookLoading(true)
      if (isSignUp && !legalTermsAccepted) {
        await showAlert({
          title: 'Terms required',
          message: 'Please accept the Terms of Service and Privacy Policy before continuing with Facebook.',
          type: 'default',
        })
        return
      }
      await clearStaleLocalSessionBeforeOAuth()
      clearOAuthAutoRetryState()
      // In production use PRODUCTION_APP_ORIGIN so Supabase redirects to canonical URL, not localhost
      // Web invite flows: carry the WC squad-join code on the redirect URL so it survives a
      // cross-app OAuth handoff (in-app browser → Safari). No-op (clean URL) for normal sign-ins.
      const redirectTo = appendWcJoinToOAuthRedirect(
        getOAuthRedirectUrls()?.redirectUrl ?? undefined,
        postAuthPath,
      )
      const facebookOAuthOpts = {
        provider: 'facebook' as const,
        options: redirectTo ? { redirectTo } : {},
      }
      const useLinkIdentity = isAnonymous && isSignUp
      const { data, error } =
        useLinkIdentity
          ? await supabase.auth.linkIdentity(facebookOAuthOpts)
          : await supabase.auth.signInWithOAuth(facebookOAuthOpts)
      if (error) throw error
      const handled = await completeOAuthBrowserOrWebRedirect(data, redirectTo, {
        provider: 'facebook',
        useLinkIdentity,
      })
      if (!handled) {
        await showAlert({ title: 'Error', message: 'Facebook sign-in could not start. Please try again.' })
      }
    } catch (error) {
      await showAuthError(error, 'Facebook sign-in', showAlert)
    } finally {
      oauthInFlightRef.current = false
      setFacebookLoading(false)
    }
  }, [
    showAlert,
    isSignUp,
    isAnonymous,
    legalTermsAccepted,
    postAuthPath,
    completeOAuthBrowserOrWebRedirect,
  ])

  const handleAppleAuth = useCallback(async () => {
    if (!isAppleSignInUiSupported()) return

    setErrors(INITIAL_ERRORS) // #4
    try {
      setAppleLoading(true)
      if (isSignUp && !legalTermsAccepted) {
        await showAlert({
          title: 'Terms required',
          message: 'Please accept the Terms of Service and Privacy Policy before continuing with Apple.',
          type: 'default',
        })
        return
      }

      // iOS: native Apple sheet → exchange identity token with Supabase (best UX, no browser).
      if (await isAppleNativeSignInSupported()) {
        let identityToken: string
        try {
          identityToken = await getAppleIdentityToken()
        } catch (error) {
          if (error instanceof Error && error.message === APPLE_SIGN_IN_CANCELED) return
          throw error
        }
        // Transient CDN/network failures (HTML body → "JSON Parse error") get one
        // automatic retry; the Apple identity token stays valid across it (AC-2.7).
        const { data, error } = await withTransientAuthRetry(async () => {
          const result = await supabase.auth.signInWithIdToken({
            provider: 'apple',
            token: identityToken,
          })
          if (result.error && isTransientAuthError(result.error)) throw result.error
          return result
        })
        if (error) throw error
        if (data.session) {
          await postSignupPrivacyConsentsIfNeeded(data.session.access_token)
          if (isAnonymous && isSignUp) track(ANALYTICS_EVENTS.GUEST_CONVERTED, { method: 'oauth' })
          track(isSignUp ? ANALYTICS_EVENTS.SIGN_UP_COMPLETED : ANALYTICS_EVENTS.SIGN_IN_COMPLETED)
          await replaceAppIfAccountDeletionAllows(data.session.access_token)
          return
        }
        await showAlert({ title: 'Error', message: 'Apple sign-in did not return a session. Please try again.' })
        return
      }

      // Web (non-Android): Supabase OAuth redirect (same flow as Google/Facebook).
      if (oauthInFlightRef.current) return
      oauthInFlightRef.current = true
      await clearStaleLocalSessionBeforeOAuth()
      clearOAuthAutoRetryState()
      // Web invite flows: carry the WC squad-join code on the redirect URL so it survives a
      // cross-app OAuth handoff (in-app browser → Safari). No-op (clean URL) for normal sign-ins.
      const redirectTo = appendWcJoinToOAuthRedirect(
        getOAuthRedirectUrls()?.redirectUrl ?? undefined,
        postAuthPath,
      )
      const appleOAuthOpts = {
        provider: 'apple' as const,
        options: redirectTo ? { redirectTo } : {},
      }
      const useLinkIdentity = isAnonymous && isSignUp
      const { data, error } =
        useLinkIdentity
          ? await supabase.auth.linkIdentity(appleOAuthOpts)
          : await supabase.auth.signInWithOAuth(appleOAuthOpts)
      if (error) throw error
      const handled = await completeOAuthBrowserOrWebRedirect(data, redirectTo, {
        provider: 'apple',
        useLinkIdentity,
      })
      if (!handled) {
        await showAlert({ title: 'Error', message: 'Apple sign-in could not start. Please try again.' })
      }
    } catch (error) {
      await showAuthError(error, 'Apple sign-in', showAlert)
    } finally {
      oauthInFlightRef.current = false
      setAppleLoading(false)
    }
  }, [
    showAlert,
    track,
    isSignUp,
    isAnonymous,
    legalTermsAccepted,
    postAuthPath,
    replaceAppIfAccountDeletionAllows,
    postSignupPrivacyConsentsIfNeeded,
    completeOAuthBrowserOrWebRedirect,
  ])

  const handleMicrosoftAuth = useCallback(async () => {
    setErrors(INITIAL_ERRORS)
    if (oauthInFlightRef.current) return
    oauthInFlightRef.current = true
    try {
      setMicrosoftLoading(true)
      if (isSignUp && !legalTermsAccepted) {
        await showAlert({
          title: 'Terms required',
          message: 'Please accept the Terms of Service and Privacy Policy before continuing with Microsoft.',
          type: 'default',
        })
        return
      }
      await clearStaleLocalSessionBeforeOAuth()
      clearOAuthAutoRetryState()
      // Web invite flows: carry the WC squad-join code on the redirect URL so it survives a
      // cross-app OAuth handoff (in-app browser → Safari). No-op (clean URL) for normal sign-ins.
      const redirectTo = appendWcJoinToOAuthRedirect(
        getOAuthRedirectUrls()?.redirectUrl ?? undefined,
        postAuthPath,
      )
      const microsoftOAuthOpts = {
        provider: 'azure' as const,
        options: redirectTo ? { redirectTo } : {},
      }
      const useLinkIdentity = isAnonymous && isSignUp
      const { data, error } =
        useLinkIdentity
          ? await supabase.auth.linkIdentity(microsoftOAuthOpts)
          : await supabase.auth.signInWithOAuth(microsoftOAuthOpts)
      if (error) throw error
      const handled = await completeOAuthBrowserOrWebRedirect(data, redirectTo, {
        provider: 'azure',
        useLinkIdentity,
      })
      if (!handled) {
        await showAlert({ title: 'Error', message: 'Microsoft sign-in could not start. Please try again.' })
      }
    } catch (error) {
      await showAuthError(error, 'Microsoft sign-in', showAlert)
    } finally {
      oauthInFlightRef.current = false
      setMicrosoftLoading(false)
    }
  }, [
    showAlert,
    isSignUp,
    isAnonymous,
    legalTermsAccepted,
    postAuthPath,
    completeOAuthBrowserOrWebRedirect,
  ])

  // #3 + #6: all back handlers now use resetFlowState as the single source of truth
  const handleToggleMode = useCallback(() => {
    setIsSignUp(prev => !prev)
    setEmail('')
    setMarketingEmailOptIn(false)
    setLegalTermsAccepted(false)
    setSwitchedToSignInBanner(null)
    resetFlowState()
  }, [resetFlowState])

  const handleBackToSignUp = useCallback(() => {
    setAwaitingVerification(false)
    setVerificationCode('')
  }, [])

  const handleBackToSignIn = useCallback(() => {
    setAwaitingSecondFactor(false)
    setSecondFactorCode('')
    setSecondFactorStrategy(null)
  }, [])

  const handleForgotPassword = useCallback(async () => {
    logger.debug('handleForgotPassword start')
    setErrors(prev => ({ ...prev, email: '', general: '', passwordReset: '' }))
    // A (re)send issues a fresh recovery code, invalidating any previously verified one.
    recoverySessionEstablishedRef.current = false

    const emailValidation = validateEmailFormat(email)
    if (!emailValidation.isValid) {
      setErrors(prev => ({ ...prev, email: emailValidation.error || 'Please enter your email address' }))
      return
    }

    const normalizedEmail = sanitizeEmailInput(email).toLowerCase()
    const last = lastPasswordResetRequest.current
    if (last && last.email === normalizedEmail && Date.now() - last.requestedAt < PASSWORD_RESET_COOLDOWN_MS) {
      const secondsLeft = Math.ceil((PASSWORD_RESET_COOLDOWN_MS - (Date.now() - last.requestedAt)) / 1000)
      await showAlert({
        title: 'Email already sent',
        message: `${PASSWORD_RESET_COOLDOWN_MESSAGE} Try again in ${secondsLeft}s.`,
      })
      return
    }

    try {
      setForgotPasswordLoading(true)
      const captchaToken = await getCaptchaToken()
      const redirectTo = getPasswordResetRedirectUrl()
      const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, { redirectTo, captchaToken })
      if (error) throw error
      lastPasswordResetRequest.current = { email: normalizedEmail, requestedAt: Date.now() }
      setAwaitingPasswordReset(true)
    } catch (error) {
      const errorMessage = getUserFriendlyErrorMessage(error)
      setErrors(prev => ({ ...prev, email: errorMessage }))
    } finally {
      resetCaptcha()
      setForgotPasswordLoading(false)
    }
  }, [email, showAlert, getCaptchaToken, resetCaptcha])

  /**
   * Completes the OTP-based password reset: verify the 6-digit recovery code, set the new
   * password, then sign the transient recovery session out and return to sign-in.
   *
   * The whole flow runs while `awaitingPasswordReset` is true, which the sign-in screen uses to
   * suppress its auto-redirect — otherwise verifyOtp's recovery session would bounce the user
   * into the app before they finish setting a password.
   */
  const handlePasswordResetSubmit = useCallback(
    async (code: string, newPassword: string) => {
      setErrors(prev => ({ ...prev, passwordReset: '' }))

      const trimmedCode = code.trim()
      if (trimmedCode.length !== 6 || !/^\d{6}$/.test(trimmedCode)) {
        setErrors(prev => ({ ...prev, passwordReset: 'Please enter the 6-digit code from your email' }))
        return
      }
      const passwordValidation = validatePassword(newPassword, true)
      if (!passwordValidation.isValid) {
        setErrors(prev => ({ ...prev, passwordReset: passwordValidation.error || 'Please enter a valid password' }))
        return
      }

      const normalizedEmail = sanitizeEmailInput(email).toLowerCase()
      try {
        setLoading(true)
        // verifyOtp consumes the single-use recovery code. Guard with the ref so a failed
        // updateUser (e.g. pwned password) lets the user retry without requesting a new code.
        if (!recoverySessionEstablishedRef.current) {
          const captchaToken = await getCaptchaToken()
          const { error: otpError } = await supabase.auth.verifyOtp({
            email: normalizedEmail,
            token: trimmedCode,
            type: 'recovery',
            options: { captchaToken },
          })
          if (otpError) throw otpError
          recoverySessionEstablishedRef.current = true
        }
        const { error: updateError } = await supabase.auth.updateUser({ password: newPassword })
        if (updateError) throw updateError
        // Invalidate the recovery session so the code/session cannot be reused.
        await supabase.auth.signOut({ scope: 'global' })
        recoverySessionEstablishedRef.current = false
        resetFlowState()
        setSwitchedToSignInBanner('Your password has been updated. Please sign in with your new password.')
      } catch (error) {
        setErrors(prev => ({ ...prev, passwordReset: getUserFriendlyErrorMessage(error) }))
      } finally {
        resetCaptcha()
        setLoading(false)
      }
    },
    [email, getCaptchaToken, resetCaptcha, resetFlowState],
  )

  // #3: clears password + all flow state + errors. If the user verified a code but bailed before
  // setting a password, sign the live recovery session out so they aren't left signed in.
  const handleBackFromPasswordReset = useCallback(async () => {
    if (recoverySessionEstablishedRef.current) {
      try {
        await supabase.auth.signOut({ scope: 'global' })
      } catch {
        // ignore — still leave the reset screen
      }
      recoverySessionEstablishedRef.current = false
    }
    resetFlowState()
  }, [resetFlowState])

  return {
    email,
    setEmail: handleEmailChange,
    password,
    setPassword: handlePasswordChange,
    loading,
    googleLoading,
    facebookLoading,
    appleLoading,
    microsoftLoading,
    forgotPasswordLoading,
    isSignUp,
    setIsSignUp,
    awaitingVerification,
    verificationCode,
    setVerificationCode: handleVerificationCodeChange,
    awaitingSecondFactor,
    secondFactorCode,
    setSecondFactorCode: handleSecondFactorCodeChange,
    secondFactorStrategy,
    errors,
    isSignedIn,
    isAnonymous,
    isLoaded,
    awaitingPasswordReset,
    switchedToSignInBanner,
    marketingEmailOptIn,
    setMarketingEmailOptIn,
    legalTermsAccepted,
    setLegalTermsAccepted,
    handleEmailAuth,
    handleVerificationCode,
    handleSecondFactor,
    handleGoogleAuth,
    handleFacebookAuth,
    handleAppleAuth,
    handleMicrosoftAuth,
    handleForgotPassword,
    handlePasswordResetSubmit,
    handleBackFromPasswordReset,
    handleToggleMode,
    handleBackToSignUp,
    handleBackToSignIn,
  }
}
